import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth, trackEvent } from '../firebase';
import AuthLayout from '../components/AuthLayout';

/** Map Firebase Auth error codes to user-friendly messages.
 *  Deliberately avoids confirming whether an email address exists. */
function getAuthErrorMessage(err: unknown): string {
  const code = (err as { code?: string }).code ?? '';
  switch (code) {
    // Merge wrong-password and user-not-found so we never confirm whether an
    // account exists for a given email.
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-email':
      return 'Invalid email or password. Please check your credentials and try again.';

    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';

    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please wait a moment before trying again.';

    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';

    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return '';

    case 'auth/popup-blocked':
      return 'Pop-up was blocked by your browser. Please allow pop-ups for this site.';

    default:
      return 'Something went wrong. Please try again.';
  }
}

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      trackEvent('login_email');
      navigate('/', { replace: true });
    } catch (err: unknown) {
      const msg = getAuthErrorMessage(err);
      if (msg) {
        trackEvent('login_failed', { method: 'email' });
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError(null);
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
      trackEvent('login_google');
      navigate('/', { replace: true });
    } catch (err: unknown) {
      const msg = getAuthErrorMessage(err);
      if (msg) {
        trackEvent('login_failed', { method: 'google' });
        setError(msg);
      }
    }
  };

  return (
    <AuthLayout>
      <div className="w-full max-w-sm mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Welcome back</h1>
        <p className="text-sm text-gray-500 mb-8">Sign in to your account to continue.</p>

        <form onSubmit={handleEmailSignIn} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="border border-gray-300 rounded-lg px-3 py-2.5 text-base outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="border border-gray-300 rounded-lg px-3 py-2.5 text-base outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors"
            />
          </label>

          {error && <p className="text-red-500 text-sm m-0">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-brand-500 hover:bg-brand-600 text-white border-0 rounded-full text-base font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed mt-1"
          >
            {loading ? 'Please wait…' : 'Sign in'}
          </button>
        </form>

        <div className="relative text-center text-sm text-gray-400 my-5">
          <span className="relative z-10 bg-white px-3">or</span>
          <span className="absolute inset-y-1/2 left-0 right-0 h-px bg-gray-200 -translate-y-1/2" />
        </div>

        <button
          onClick={handleGoogle}
          className="w-full py-2.5 bg-white text-gray-700 border border-gray-300 rounded-full text-base font-semibold hover:bg-gray-50 transition-colors"
        >
          Continue with Google
        </button>

        <p className="text-center text-sm text-gray-500 mt-6 mb-0">
          Don&apos;t have an account?{' '}
          <Link to="/signup" className="text-brand-500 font-semibold hover:underline">
            Sign up free
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
